import React from 'react';
import classnames from 'classnames';
import _ from 'lodash';
import Isvg from 'react-inlinesvg';

import SuccessIcon from 'app/images/icons/success.svg';
import ErrorIcon from 'app/images/icons/error.svg';
import Loader from 'app/components/general/Loader';

export const BUTTON_STATE = {
  loading: 'loading',
  disabled: 'disabled',
  confirming: 'confirming',
  success: 'success',
  error: 'error',
  nothing: '',
};

interface IButtonProps {
  active?: boolean;
  block?: boolean;
  color?: string;
  tabIndex?: string;
  disabled?: boolean;
  noLoading?: boolean;
  isLoading?: boolean;
  outline?: boolean;
  tag?: any;
  getRef?: any;
  onClick?: Function;
  size?: string;
  type: 'button' | 'submit' | 'reset' | undefined;
  children?: React.ReactNode;
  className?: string;
  cssModule?: Function;
  icon?: string;
  loadingColor?: string;
  btnIcon?: any;
  centerloader?: boolean;
  confirmButton?: boolean;
  hasSaved?: boolean;
  hasFailedValidation?: boolean;
  hasFailed?: boolean;
  savedDuration?: number;
  loadingText?: string;
  errorText?: string;
  successText?: string;
  delaySubmit?: boolean;
}

interface IButtonState {
  currentState: string;
  transitioned: boolean;
  mounted: boolean;
}

export default class Button extends React.Component<IButtonProps, IButtonState> {
  timeout;

  confirmTimeout;

  localLoadingTimeout;

  static defaultProps: IButtonProps = {
    color: 'secondary',
    tag: 'button',
    noLoading: false,
    isLoading: false,
    centerloader: true,
    icon: undefined,
    cssModule: undefined,
    getRef: undefined,
    outline: undefined,
    disabled: undefined,
    tabIndex: undefined,
    block: undefined,
    active: undefined,
    size: undefined,
    type: 'button',
    loadingColor: undefined,
    onClick: undefined,
    btnIcon: undefined,
    hasSaved: false,
    confirmButton: false,
    hasFailedValidation: false,
    hasFailed: false,
    delaySubmit: false,
    savedDuration: 2500,
    loadingText: 'Processing',
    errorText: 'Failed',
    successText: 'Success',
  };

  state: IButtonState = {
    currentState: BUTTON_STATE.nothing,
    mounted: false,
    transitioned: false,
  }

  static getDerivedStateFromProps(nextProps, prevState): any {
    // console.log(prevState);
    // console.log('prevState');
    // console.log(prevState);
    // console.log('Check loading');
    // console.log((prevState.mounted && (prevState.currentState === BUTTON_STATE.loading || nextProps.isLoading)));
    if (prevState.mounted && !prevState.transitioned && !prevState.hasSaved && nextProps.hasSaved) {
      return {
        currentState: BUTTON_STATE.success,
        transitioned: false,
      };
    }
    if (prevState.mounted && !prevState.transitioned && !prevState.hasFailed && nextProps.hasFailed) {
      return {
        currentState: BUTTON_STATE.error,
        transitioned: false,
      };
    }
    if (prevState.mounted && (nextProps.hasFailedValidation && !nextProps.isLoading)) {
      return {
        currentState: BUTTON_STATE.nothing,
        transitioned: false,
      };
    }
    if (prevState.mounted && (prevState.currentState === BUTTON_STATE.loading || nextProps.isLoading)) {
      return {
        currentState: BUTTON_STATE.loading,
        transitioned: false,
      };
    }
    if (prevState.mounted && (prevState.currentState === BUTTON_STATE.confirming)) {
      return {
        currentState: BUTTON_STATE.confirming,
        transitioned: false,
      };
    }
    return {
      currentState: BUTTON_STATE.nothing,
    };
  }

  componentDidMount = () => {
    this.setState({
      currentState: BUTTON_STATE.nothing,
      mounted: true,
      transitioned: true,
    });
  }

  componentDidUpdate = (prevProps, prevState) => {
    // console.log(this.state.currentState);
    // console.log(prevState.currentState);
    if (this.state.mounted && prevState.currentState !== BUTTON_STATE.success && this.state.currentState === BUTTON_STATE.success) {
      this.success();
    }
    if (this.state.mounted && prevState.currentState !== BUTTON_STATE.error && this.state.currentState === BUTTON_STATE.error) {
      this.error();
    }

    // Reset button if Processing in button, but nothing submitted by parent
    if (this.state.mounted && this.state.currentState === BUTTON_STATE.loading && this.props.isLoading === false && this.localLoadingTimeout === undefined) {
      this.localLoadingTimeout = setTimeout(() => {
        this.setState({ currentState: BUTTON_STATE.nothing, transitioned: false });
        clearTimeout(this.localLoadingTimeout);
        this.localLoadingTimeout = undefined;
      }, 3000);
    }
    if (this.state.mounted && this.state.currentState === BUTTON_STATE.loading && this.props.isLoading === true) {
      clearTimeout(this.localLoadingTimeout);
      this.localLoadingTimeout = undefined;
    }

    if (this.state.mounted && this.state.currentState === BUTTON_STATE.confirming && this.props.isLoading === false && this.confirmTimeout === undefined) {
      this.confirmTimeout = setTimeout(() => {
        this.setState({ currentState: BUTTON_STATE.nothing, transitioned: true });
        clearTimeout(this.confirmTimeout);
        this.confirmTimeout = undefined;
      }, 3000);
    }
    if (this.state.mounted && this.state.currentState === BUTTON_STATE.confirming && this.props.isLoading === true) {
      clearTimeout(this.confirmTimeout);
    }
  }

  componentWillUnmount(): void {
    clearTimeout(this.timeout);
    clearTimeout(this.localLoadingTimeout);
    clearTimeout(this.confirmTimeout);
  }

  success = (): void => {
    this.timeout = setTimeout(() => {
      this.setState({ currentState: BUTTON_STATE.nothing, transitioned: true });
    }, this.props.savedDuration);
  }

  error = (): void => {
    this.timeout = setTimeout(() => {
      this.setState({ currentState: BUTTON_STATE.nothing, transitioned: true });
    }, this.props.savedDuration);
  }

  handleClick = (e): void => {
    e.persist();
    // clearTimeout(this.timeout);
    if (this.props.disabled) {
      e.preventDefault();
      return;
    }
    const shouldAllowClick = (this.state.currentState !== BUTTON_STATE.loading)
      && this.state.currentState !== BUTTON_STATE.disabled;
    if (shouldAllowClick) {
      let switchState = this.props.noLoading ? BUTTON_STATE.nothing : BUTTON_STATE.loading;
      if (this.props.confirmButton && this.state.currentState !== BUTTON_STATE.confirming) {
        switchState = BUTTON_STATE.confirming;
      }
      this.setState({ currentState: switchState }, async () => {
        if (switchState !== BUTTON_STATE.confirming && this.props.onClick !== undefined) {
          if (this.props.delaySubmit) {
            setTimeout(() => {
              this.props.onClick(e);
            }, 150);
          } else {
            this.props.onClick(e);
          }
        }
      });
    } else {
      e.preventDefault();
    }
  }

  renderLoading = (buttonAttributes: any): React.ReactNode => (
    <button
      ref={this.props.getRef}
      onClick={this.handleClick}
      type={this.props.type}
      className={classnames('btn', 'loading', this.props.className, {
      })}
      {...buttonAttributes}
    >
      {/* {this.props.btnIcon && (<Isvg className="btn-icon" src={this.props.btnIcon} />)} */}
      <Loader
        isActive
        className="smaller"
        color={this.props.loadingColor}
      />
      <span
        className={classnames('align-middle', {
          // 'btn-transparent-text':
          //   this.props.centerloader,
        })}
      >
        {this.props.loadingText}
      </span>
    </button>
  )

  renderSuccess = (buttonAttributes: any): React.ReactNode => (
    <button
      ref={this.props.getRef}
      onClick={this.handleClick}
      type={this.props.type}
      className={classnames('btn', this.props.className, 'success-state', {
        'btn-center-loader': this.props.centerloader,
      })}
      {...buttonAttributes}
    >
      <Isvg className="btn-icon" src={SuccessIcon} />
      <span
        className={classnames('align-middle')}
      >
        {this.props.successText}
      </span>
    </button>
  )

  renderError = (buttonAttributes: any): React.ReactNode => (
    <button
      ref={this.props.getRef}
      onClick={this.handleClick}
      type={this.props.type}
      className={classnames('btn', this.props.className, 'error-state', {
        'btn-center-loader': this.props.centerloader,
      })}
      {...buttonAttributes}
    >
      <Isvg className="btn-icon" src={ErrorIcon} />
      <span
        className={classnames('align-middle')}
      >
        {this.props.errorText}
      </span>
    </button>
  )

  renderNormal = (buttonAttributes: any): React.ReactNode => (
    <button
      ref={this.props.getRef}
      onClick={this.handleClick}
      type={this.props.type}
      className={classnames('btn', this.props.className, {
        'btn-center-loader': this.props.centerloader,
      })}
      {...buttonAttributes}
    >
      {this.props.btnIcon && (<Isvg className="btn-icon" src={this.props.btnIcon} />)}
      <span
        className={classnames('align-middle')}
      >
        {this.props.children}
      </span>
    </button>
  )

  renderConfirming = (buttonAttributes: any): React.ReactNode => (
    <button
      ref={this.props.getRef}
      onClick={this.handleClick}
      type={this.props.type}
      className={classnames('btn', this.props.className, {
        'btn-center-loader': this.props.centerloader,
      })}
      {...buttonAttributes}
    >
      {this.props.btnIcon && (<Isvg className="btn-icon" src={this.props.btnIcon} />)}
      <span
        className={classnames('align-middle')}
      >
        Are you sure?
      </span>
    </button>
  )

  render(): React.ReactNode {
    const {
      ...attributes
    } = this.props;
    const buttonAttributes = _.omit(attributes, ['delaySubmit', 'onClick', 'centerloader', 'savedDuration', 'active', 'block', 'className', 'cssModule', 'color', 'outline', 'size', 'type', 'tag', 'getRef', 'isLoading', 'loadingColor', 'btnIcon', 'loadingText', 'errorText', 'successText']);
    switch (this.state.currentState) {
      case BUTTON_STATE.loading:
        return this.renderLoading(buttonAttributes);
      case BUTTON_STATE.confirming:
        return this.renderConfirming(buttonAttributes);
      case BUTTON_STATE.success:
        return this.renderSuccess(buttonAttributes);
      case BUTTON_STATE.error:
        return this.renderError(buttonAttributes);
      default:
        return this.renderNormal(buttonAttributes);
    }
  }
}
